import { isEqual, isObject } from 'helpers/global.helper/global.helper'

type DbsParts = {
  instruction: Partial<DatasetInstruction>
  contract: Record<string, unknown>
  metadataFilter: Record<string, unknown>
  include: Record<string, unknown>
}

function toDbsParts(instruction: Partial<DatasetInstruction>): DbsParts | undefined {
  const contract: unknown = instruction.contract
  if (instruction.type !== 'dbs' || !isObject(contract) || !isObject(contract.metadata_filter)) {
    return undefined
  }
  const metadataFilter = contract.metadata_filter

  return {
    instruction,
    contract,
    metadataFilter,
    include: isObject(metadataFilter.include) ? metadataFilter.include : {},
  }
}

function toDbsInstruction({ instruction, contract, metadataFilter, include }: DbsParts): Partial<DatasetInstruction> {
  return {
    ...instruction,
    contract: { ...contract, metadata_filter: { ...metadataFilter, include } },
  } as Partial<DatasetInstruction> // conventions-allow: unsafe-cast — the contract was read as a DbS one
}

/** Sets the instruction's `optjob_subtype` filter to `subtypes`, or removes it when there are none. */
export function withSubtypes(
  instruction: Partial<DatasetInstruction>,
  subtypes: string[]
): Partial<DatasetInstruction> {
  const parts = toDbsParts(instruction)
  if (!parts) {
    return instruction
  }
  const { optjob_subtype: _replaced, ...include } = parts.include

  return toDbsInstruction({ ...parts, include: subtypes.length ? { ...include, optjob_subtype: subtypes } : include })
}

function toRequestShape({ instruction, contract, metadataFilter, include }: DbsParts): unknown {
  const { id_renaming: _idRenaming, ...otherContract } = contract
  const { internal_id: internalId, ...otherInclude } = include
  const { return_id: _returnId, ...otherInstruction } = instruction

  return {
    ...otherInstruction,
    contract: {
      ...otherContract,
      metadata_filter: { ...metadataFilter, include: { ...otherInclude, internal_id: internalId === undefined } },
    },
  }
}

function toRenaming({ contract }: DbsParts): Record<string, unknown> {
  return isObject(contract.id_renaming) ? contract.id_renaming : {}
}

function canMergeRenamings(a: DbsParts, b: DbsParts): boolean {
  const renamingOfA = toRenaming(a)
  const renamingOfB = toRenaming(b)

  return Object.keys(renamingOfB).every(
    (internalId) => !(internalId in renamingOfA) || isEqual(renamingOfA[internalId], renamingOfB[internalId])
  )
}

function mergeInto(target: DbsParts, other: DbsParts): DbsParts {
  const internalIds =
    target.include.internal_id === undefined
      ? {}
      : { internal_id: [...new Set([target.include.internal_id, other.include.internal_id].flat())] }
  const idRenaming =
    target.contract.id_renaming === undefined && other.contract.id_renaming === undefined
      ? {}
      : { id_renaming: { ...toRenaming(target), ...toRenaming(other) } }

  return { ...target, contract: { ...target.contract, ...idRenaming }, include: { ...target.include, ...internalIds } }
}

type MergeEntry = { instruction: Partial<DatasetInstruction>; parts?: DbsParts }

function canMerge(entry: MergeEntry, parts: DbsParts): boolean {
  return (
    !!entry.parts &&
    canMergeRenamings(entry.parts, parts) &&
    isEqual(toRequestShape(entry.parts), toRequestShape(parts))
  )
}

/** Merges DbS instructions that differ only in `internal_id` and `id_renaming`, at the first one's place. */
export function mergeDbsInstructionsOverUnits(
  instructions: Partial<DatasetInstruction>[]
): Partial<DatasetInstruction>[] {
  const entries: MergeEntry[] = []

  for (const instruction of instructions) {
    const parts = toDbsParts(instruction)
    const target = parts && entries.find((entry) => canMerge(entry, parts))

    if (parts && target?.parts) {
      target.parts = mergeInto(target.parts, parts)
      target.instruction = toDbsInstruction(target.parts)
    } else {
      entries.push({ instruction, parts })
    }
  }

  return entries.map(({ instruction }) => instruction)
}
